import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Menu, X } from "lucide-react";
import Scene from "./observatory/Scene";
import ThemeToggle from "./ThemeToggle";
import { MotionToggle } from "./JourneyUX";
import logo from "../assets/images/logo.png";
import { whatsapp } from "./catalog";
import { formatDiscountDate, formatDiscountDateTime } from "../hooks/useActiveDiscount";
import usePricing from "./PricingContext";
import { businessProfileUrl } from "../seo/site";

export function Arrow({ diagonal = false }: { diagonal?: boolean }) {
  return diagonal ? (
    <ArrowUpRight size={18} aria-hidden="true" />
  ) : (
    <ArrowRight size={18} aria-hidden="true" />
  );
}
export function Brand() {
  return (
    <Link className="brand" to="/" aria-label="Radian Soul home">
      <img src={logo} alt="" />
      <span>
        Radian Soul<small>JOURNEY WITHIN</small>
      </span>
    </Link>
  );
}
export function Header() {
  const [open,setOpen]=useState(false);
  const dialog=useRef<HTMLDialogElement>(null);
  const location=useLocation();
  useEffect(()=>{dialog.current?.close();setOpen(false);},[location]);
  const close=()=>{dialog.current?.close();setOpen(false);};
  return <header className="site-header observatory-header">
    <a className="skip-link" href="#main">Skip to content</a>
    <div className="container header-inner"><Brand/><nav className="header-shortcuts" aria-label="Quick navigation"><NavLink to="/services">The collection</NavLink><NavLink to="/journal">The journal</NavLink></nav><ThemeToggle/><button className="observatory-menu-toggle" onClick={()=>{dialog.current?.showModal();setOpen(true);}} aria-label="Open navigation" aria-expanded={open} aria-controls="primary-navigation"><span>Explore</span><Menu size={21}/></button></div>
    <dialog ref={dialog} className="navigation-world" onCancel={()=>setOpen(false)} aria-label="Explore Radian Soul">
      <div className="navigation-world-top"><Brand/><button className="dialog-close" onClick={close} aria-label="Close navigation"><X/></button></div>
      <div className="navigation-world-grid"><div className="navigation-world-art"><Scene slug="past-life"/><p>There is more<br/>to <em>discover.</em></p></div><nav id="primary-navigation" aria-label="Main navigation">{[['The collection','/services'],['Tarot & guidance','/services/tarot'],['Healing practices','/services/healing'],['Learn with Vaanii','/services/courses'],['Meet Vaanii','/about'],['Vaanii’s Journal','/journal']].map(([title,path],i)=><NavLink key={path} to={path} onClick={close}><span>0{i+1}</span>{title}<Arrow diagonal/></NavLink>)}<a className="navigation-contact" href={whatsapp()} target="_blank" rel="noreferrer">A conversation with Vaanii ↗</a></nav></div>
      <div className="mobile-menu-tools"><button onClick={()=>{close();window.dispatchEvent(new Event('radian-find-path'));}}>Find my path <Arrow/></button><MotionToggle/></div>
    </dialog>
  </header>;
}
export function Footer() {
  return (
    <footer className="site-footer observatory-footer">
      <div className="container">
        <div className="footer-wordmark" aria-hidden="true">Radian <em>Soul</em><span>✧</span></div>
        <div className="footer-top">
          <div>
            <p className="eyebrow">Here when you need us</p>
            <h2>A conversation is a good start.</h2>
            <p>
              Have a question about a session?
              <br />
              You can message Vaanii directly.
            </p>
          </div>
          <a
            className="button"
            href={whatsapp()}
            target="_blank"
            rel="noreferrer"
          >
            Connect on WhatsApp <Arrow diagonal />
          </a>
        </div>
        <div className="footer-middle">
          <Brand />
          <div className="footer-links">
            <Link to="/services">Services</Link>
            <Link to="/services/courses">Courses</Link>
            <Link to="/about">About Vaanii</Link>
            <Link to="/journal">Vaanii’s Journal</Link>
          </div>
          <div className="footer-contact">
            <a href="mailto:support@radiansoul.com">support@radiansoul.com</a>
            <a href="tel:+917375015958">+91 73750 15958</a>
            <a href={businessProfileUrl} target="_blank" rel="noreferrer">Find us on Google ↗</a>
          </div>
        </div>
        <div className="footer-bottom">
          <span>© {new Date().getFullYear()} Radian Soul</span>
          <p>
            Spiritual services offer guidance and do not replace medical,
            financial, or legal advice.
          </p>
          <div>
            <a
              href="https://www.instagram.com/radiansoul03"
              target="_blank"
              rel="noreferrer"
            >
              Instagram ↗
            </a>
            <a
              href="https://www.youtube.com/@Radiansoul"
              target="_blank"
              rel="noreferrer"
            >
              YouTube ↗
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
export function DiscountNote() {
  const { activeCampaign } = usePricing();
  if (!activeCampaign) return null;
  return (
    <aside className="discount-note">
      <strong>
        {activeCampaign.name} · {activeCampaign.percent}% off
      </strong>
      <span>
        Applied automatically to every service through{" "}
        {activeCampaign.id === 'navratri-2026' ? `${formatDiscountDate(activeCampaign.endAt)}, 11:59 PM IST` : `${formatDiscountDateTime(activeCampaign.endAt)} IST`}.
      </span>
    </aside>
  );
}
export function Breadcrumb({
  current,
  parent = "Services",
  parentPath = "/services",
}: {
  current: string;
  parent?: string;
  parentPath?: string;
}) {
  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      <Link to="/">Home</Link>
      <span aria-hidden="true">/</span>
      <Link to={parentPath}>{parent}</Link>
      <span aria-hidden="true">/</span>
      <span aria-current="page">{current}</span>
    </nav>
  );
}
export function HowItWorks() {
  return (
    <section className="how-section container">
      <div>
        <p className="eyebrow">A SIMPLE BEGINNING</p>
        <h2>
          Your space.
          <br />
          Your pace.
        </h2>
        <p>
          From choosing a session to your first conversation, here is what to
          expect.
        </p>
      </div>
      <ol className="steps">
        <li>
          <span>01</span>
          <div>
            <h3>Find what feels right</h3>
            <p>
              Explore the services and choose a format. If you’re unsure, ask
              Vaanii for guidance.
            </p>
          </div>
        </li>
        <li>
          <span>02</span>
          <div>
            <h3>Choose your session</h3>
            <p>
              Review the price and session details. You can check availability
              on WhatsApp before paying.
            </p>
          </div>
        </li>
        <li>
          <span>03</span>
          <div>
            <h3>Confirm and connect</h3>
            <p>
              After payment, share your confirmation on WhatsApp to arrange your
              session and preparation.
            </p>
          </div>
        </li>
      </ol>
    </section>
  );
}
